// D2 — Resumen Semanal del directivo.
//
// Cadencia semanal (~30 min, reunión con coordinadores): comparativo cross-planta
// de la semana. Gráfico cumplimiento por planta (programa vs meta interna), tabla
// comparativa y eventos destacados derivados. SOLO LECTURA.
//
// Rediseño 2026-07-05 (vuelve al menú cuando el responsable la valide):
//   - Formatos es-CO (porcentajes enteros, deltas en pp, horas con coma).
//   - Guard F1 en la tendencia: cumplimiento > 200% (programa ínfimo) → null
//     (el eje llegaba a 9.624% con semanas de programa residual).
//   - DESCARGA REAL del "Informe ejecutivo de producción" con marca Jidokap
//     (plantilla solicitada por el cliente): canvas → PNG, patrón del resumen
//     de turno del supervisor. Se compone con datos que la página ya puede
//     pedir (resumen semanal + líneas + detalle por día/hora) — sin backend
//     nuevo. Disponibilidad y rendimiento van POR SEPARADO (sin eje de
//     calidad no hay OEE honesto — decisión del responsable).

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DirectivoResumenSemanal, EstadoPlanta, ResumenPlantaSemana } from 'schemas';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { KpiCard } from '../../components/ui/kpi-card';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import {
  useDirectivoLineas,
  useDirectivoResumenDetalle,
  useDirectivoResumenSemanal,
  useMe,
} from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { cn } from '../../lib/utils';
import { PeriodoNav, inicioSemanaActual, shiftDias } from './components/PeriodoNav';
import { descargarInformeEjecutivo, type InformeDatos } from './informeEjecutivo';
import { leerSemanaParam } from './lib/urlParams';

// Paleta categórica por planta (misma convención que AnalisisParosPage).
const PLANTA_COLORS = [
  'var(--color-accent)',
  'var(--color-info)',
  'var(--color-warn)',
  'var(--color-ok)',
  'var(--color-crit)',
  'var(--color-fg-tertiary)',
];

function miles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

function copMTexto(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `$${(n / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  return `$${miles(n)}`;
}

function rangoLabel(desde: string, hasta: string): string {
  const fmt = (iso: string, withYear = false) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    const date = new Date(Date.UTC(y!, m! - 1, d!));
    return new Intl.DateTimeFormat('es-CO', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
      .format(date)
      .replace(/\./g, '');
  };
  return `${fmt(desde)} – ${fmt(hasta, true)}`;
}

const ESTADO_META: Record<
  EstadoPlanta,
  { label: string; tone: 'ok' | 'warn' | 'crit' | 'neutral'; cssVar: string }
> = {
  ok: { label: 'OK', tone: 'ok', cssVar: 'var(--color-ok)' },
  warn: { label: 'Atención', tone: 'warn', cssVar: 'var(--color-warn)' },
  crit: { label: 'Crítico', tone: 'crit', cssVar: 'var(--color-crit)' },
  sin_data: { label: 'S/D', tone: 'neutral', cssVar: 'var(--color-fg-tertiary)' },
};

const DOT_COLOR: Record<EstadoPlanta, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
  sin_data: 'bg-fg-tertiary',
};

/** Día corto es-CO para los buckets del informe ('2026-06-29' → 'lun 29'). */
function diaCorto(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\.|,/g, '');
}

export function D2ResumenSemanalPage() {
  // La semana vive en la URL (?semana=YYYY-MM-DD, misma fecha que recibe la API):
  // refrescar / atrás la conservan. Ausente o inválida → semana en curso (null).
  const [searchParams, setSearchParams] = useSearchParams();
  const semana = leerSemanaParam(searchParams.get('semana'));
  const setSemana = (s: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (s) next.set('semana', s);
        else next.delete('semana');
        return next;
      },
      { replace: true },
    );
  const [plantaSel, setPlantaSel] = useState<string>('todas');
  const [descargando, setDescargando] = useState(false);
  const { data, isLoading, isError } = useDirectivoResumenSemanal(semana);
  const me = useMe();
  const { diaInicioSemana, hoyOperativo } = useTenantConfig();
  // Insumos del informe ejecutivo (mismos endpoints del mundo directivo):
  // pareto/horas/$ por línea + serie por día + perfil horario, sobre la semana.
  const rango = data ? { desde: data.semana.desde, hasta: data.semana.hasta } : null;
  const inicioSemana = inicioSemanaActual(diaInicioSemana, hoyOperativo);
  const lineas = useDirectivoLineas({
    desde: rango?.desde ?? inicioSemana,
    hasta: rango?.hasta ?? inicioSemana,
    turno: null,
  });
  const detalleDia = useDirectivoResumenDetalle({
    desde: rango?.desde ?? inicioSemana,
    hasta: rango?.hasta ?? inicioSemana,
    granularidad: 'dia',
    turno: null,
  });
  const detalleHora = useDirectivoResumenDetalle({
    desde: rango?.desde ?? inicioSemana,
    hasta: rango?.hasta ?? inicioSemana,
    granularidad: 'hora_dia',
    turno: null,
  });

  const informeListo = Boolean(data && lineas.data && detalleDia.data && detalleHora.data);

  const descargarInforme = async () => {
    if (!data || !lineas.data || !detalleDia.data || !detalleHora.data) return;
    setDescargando(true);
    try {
      const lin = lineas.data;
      const rangoTxt = rangoLabel(data.semana.desde, data.semana.hasta);
      const plantasLabel =
        `${me.data?.tenant?.nombre ?? ''} · ${data.por_planta.length} plantas`.replace(/^ · /, '');
      const descargadoPor = me.data?.user ? `${me.data.user.nombre} ${me.data.user.apellido}` : '—';

      // Arma una página del informe: consolidado (plantaId null) o una planta.
      const armarPagina = (plantaId: string | null): InformeDatos => {
        const filas = lin.lineas.filter((l) => plantaId === null || l.planta_id === plantaId);
        const pareto = lin.pareto.filter((c) => plantaId === null || c.planta_id === plantaId);
        const serieDia = detalleDia.data!.serie.filter(
          (r) => plantaId === null || r.planta_id === plantaId,
        );
        const serieHora = detalleHora.data!.serie.filter(
          (r) => plantaId === null || r.planta_id === plantaId,
        );
        const row =
          plantaId !== null ? data.por_planta.find((x) => x.planta_id === plantaId) : null;

        const horasTrab = filas.reduce((sum, l) => sum + l.horas_trabajadas, 0);
        const paroMin = filas.reduce((sum, l) => sum + l.paros_min, 0);
        const paroH = paroMin / 60;
        const unidades = filas.reduce((sum, l) => sum + l.unidades, 0);
        const meta = filas.reduce((sum, l) => sum + l.meta_capacidad_unds, 0);
        const conCostos = lin.puede_ver_costos;
        const valor = conCostos ? filas.reduce((sum, l) => sum + (l.valor_cop ?? 0), 0) : null;
        const insumos = conCostos
          ? filas.reduce((sum, l) => sum + (l.ingreso_insumos_cop ?? 0), 0)
          : null;
        const costoMo = conCostos ? filas.reduce((sum, l) => sum + (l.costo_mo_cop ?? 0), 0) : null;
        const perdidaParos = conCostos
          ? pareto.reduce((sum, c) => sum + (c.valor_cop ?? 0), 0)
          : null;
        const valorParcial = filas.some((l) => l.skus_sin_precio > 0);

        const porDiaMap = new Map<string, { real: number; prog: number; conProg: boolean }>();
        for (const r of serieDia) {
          const acc = porDiaMap.get(r.bucket) ?? { real: 0, prog: 0, conProg: false };
          acc.real += r.produccion;
          if (r.programa_unds !== null) {
            acc.prog += r.programa_unds;
            acc.conProg = true;
          }
          porDiaMap.set(r.bucket, acc);
        }
        const porDia = [...porDiaMap.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([bucket, v]) => ({
            label: diaCorto(bucket),
            programada: v.conProg ? v.prog : null,
            real: v.real,
          }));
        const programadaTotal = porDia.some((d) => d.programada !== null)
          ? porDia.reduce((sum, d) => sum + (d.programada ?? 0), 0)
          : null;

        const porHoraMap = new Map<string, number>();
        for (const r of serieHora) {
          porHoraMap.set(r.bucket, (porHoraMap.get(r.bucket) ?? 0) + r.produccion);
        }
        const porHora = [...porHoraMap.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([h, unds]) => ({ label: h, unidades: unds }));

        const causasMap = new Map<string, number>();
        for (const c of pareto) causasMap.set(c.causal, (causasMap.get(c.causal) ?? 0) + c.minutos);
        const causas = [...causasMap.entries()]
          .map(([nombre, minutos]) => ({ nombre, minutos }))
          .sort((a, b) => b.minutos - a.minutos);

        const t = data.tendencia_semanal;
        const plantasTend =
          plantaId === null ? t.plantas : t.plantas.filter((x) => x.planta_id === plantaId);
        const tendencia: InformeDatos['tendencia'] = {
          plantas: plantasTend.map((x) => x.nombre),
          semanas: t.semanas.map((sw) => ({
            label: sw.label,
            valores: plantasTend.map((x) => {
              const punto = t.puntos.find(
                (q) => q.semana_iso === sw.semana_iso && q.planta_id === x.planta_id,
              );
              // Guard F1: >200% = programa ínfimo, no señal.
              return punto && punto.cumplimiento_pct !== null && punto.cumplimiento_pct <= 200
                ? punto.cumplimiento_pct
                : null;
            }),
          })),
        };

        const cumpl =
          plantaId === null
            ? data.kpis.cumplimiento_consolidado.valor_pct
            : (row?.cumpl_programa_pct ?? null);
        const real =
          plantaId === null ? data.kpis.produccion_total.cantidad : (row?.producido ?? unidades);

        const observaciones: string[] = [
          cumpl !== null
            ? `La producción alcanzó el ${Math.round(cumpl)} % del programa del cliente (${miles(real)} paq).`
            : `Producción de ${miles(real)} paq — sin programa del cliente cargado esta semana.`,
        ];
        if (causas[0] && causas[0].minutos > 0) {
          observaciones.push(
            `Principal causa de paro: ${causas[0].nombre} (${Math.floor(causas[0].minutos / 60)} h de ${Math.round(paroH)} h de paro total).`,
          );
        }
        if (perdidaParos !== null && perdidaParos > 0) {
          observaciones.push(
            `Los paros dejaron de producir ${copMTexto(perdidaParos)} a precios vigentes.`,
          );
        }
        if (plantaId === null) {
          const conCumpl = data.por_planta.filter((x) => x.cumpl_programa_pct !== null);
          if (conCumpl.length > 1) {
            const peor = [...conCumpl].sort(
              (a, b) => (a.cumpl_programa_pct ?? 0) - (b.cumpl_programa_pct ?? 0),
            )[0]!;
            observaciones.push(
              `Menor cumplimiento de la semana: ${peor.nombre} (${Math.round(peor.cumpl_programa_pct ?? 0)} %).`,
            );
          }
        }

        return {
          semanaLabel: `Semana ${data.semana.numero_iso}`,
          rangoLabel: rangoTxt,
          plantasLabel,
          titulo: plantaId === null ? 'Consolidado' : `Planta ${row?.nombre ?? ''}`,
          estado: row
            ? { label: ESTADO_META[row.estado].label, tone: ESTADO_META[row.estado].tone }
            : null,
          kpis: {
            programada: programadaTotal,
            real,
            cumplimiento_pct: cumpl,
            rendimiento_pct: meta > 0 ? (unidades / meta) * 100 : null,
            disponibilidad_pct: horasTrab > 0 ? ((horasTrab - paroH) / horasTrab) * 100 : null,
            paro_min: paroMin,
            valor_cop: valor,
            valor_parcial: valorParcial,
          },
          finanzas: {
            valor_cop: valor,
            insumos_cop: insumos,
            costo_mo_cop: costoMo,
            utilidad_cop: valor !== null ? valor + (insumos ?? 0) - (costoMo ?? 0) : null,
            perdida_paros_cop: perdidaParos,
          },
          porDia,
          porHora,
          causas,
          tiempo: { productivasH: Math.max(0, horasTrab - paroH), paroH },
          tendencia,
          observaciones,
          descargadoPor,
        };
      };

      // Página 1: consolidado · después una página por planta (con su estado).
      await descargarInformeEjecutivo([
        armarPagina(null),
        ...data.por_planta.map((x) => armarPagina(x.planta_id)),
      ]);
    } finally {
      setDescargando(false);
    }
  };

  if (isLoading || !data) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </DirectivoLayout>
    );
  }
  if (isError) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-crit">
          No se pudo cargar el resumen semanal.
        </div>
      </DirectivoLayout>
    );
  }

  // Filtro de planta EN CLIENTE: el payload ya viene desglosado por planta.
  const plantaRow =
    plantaSel !== 'todas' ? data.por_planta.find((x) => x.planta_id === plantaSel) : undefined;
  const dataVista: DirectivoResumenSemanal = plantaRow
    ? {
        ...data,
        por_planta: [plantaRow],
        eventos: data.eventos.filter((e) => e.planta_nombre === plantaRow.nombre),
        kpis: {
          ...data.kpis,
          cumplimiento_consolidado: {
            valor_pct: plantaRow.cumpl_programa_pct,
            // El delta del backend es consolidado — por planta no aplica.
            delta_pp: null,
          },
          produccion_total: {
            cantidad: plantaRow.producido,
            plantas: 1,
            lineas:
              lineas.data?.lineas.filter((l) => l.planta_id === plantaRow.planta_id).length ?? 0,
          },
          plantas_alerta: {
            count: plantaRow.estado === 'warn' || plantaRow.estado === 'crit' ? 1 : 0,
            total: 1,
            peor_planta_nombre: null,
            peor_linea_label: null,
          },
        },
      }
    : data;

  return (
    <DirectivoLayout>
      <div className="mx-auto flex max-w-[1280px] flex-col gap-5">
        <Header
          data={data}
          onDescargar={descargarInforme}
          descargando={descargando}
          listo={informeListo}
        />
        <Filtros data={data} onSemana={setSemana} plantaSel={plantaSel} onPlanta={setPlantaSel} />
        <KpiRow data={dataVista} />
        <TendenciaCumplimiento data={data} />
        <GraficoPorPlanta data={dataVista} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[3fr_2fr]">
          <Comparativa plantas={dataVista.por_planta} />
          <Eventos eventos={dataVista.eventos} />
        </div>
      </div>
    </DirectivoLayout>
  );
}

function Header({
  data,
  onDescargar,
  descargando,
  listo,
}: {
  data: DirectivoResumenSemanal;
  onDescargar: () => void;
  descargando: boolean;
  listo: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-[24px] font-medium leading-tight text-fg">Resumen semanal</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Semana {data.semana.numero_iso} · {data.por_planta.length} plantas ·{' '}
          {rangoLabel(data.semana.desde, data.semana.hasta)}
        </p>
      </div>
      <button
        type="button"
        onClick={onDescargar}
        disabled={!listo || descargando}
        title={
          listo ? 'Descarga el informe con marca Jidokap (PNG)' : 'Cargando datos del informe…'
        }
        className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-xs font-medium text-accent-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Icon name="download" size={14} stroke={2} />
        {descargando ? 'Generando…' : 'Descargar informe ejecutivo'}
      </button>
    </div>
  );
}

function Filtros({
  data,
  onSemana,
  plantaSel,
  onPlanta,
}: {
  data: DirectivoResumenSemanal;
  onSemana: (semana: string | null) => void;
  plantaSel: string;
  onPlanta: (id: string) => void;
}) {
  const conAlerta = data.kpis.plantas_alerta.count;
  const { diaInicioSemana, hoyOperativo } = useTenantConfig();
  const isCurrent = data.semana.desde >= inicioSemanaActual(diaInicioSemana, hoyOperativo);
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Semana
          </span>
          <PeriodoNav
            label={`Sem ${data.semana.numero_iso} · ${rangoLabel(data.semana.desde, data.semana.hasta)}`}
            isCurrent={isCurrent}
            canNext={!isCurrent}
            onPrev={() => onSemana(shiftDias(data.semana.desde, -7))}
            onNext={() => onSemana(shiftDias(data.semana.desde, 7))}
            onReset={() => onSemana(null)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Planta
          </span>
          <Select
            className="h-8 w-auto min-w-[150px] py-1 text-xs"
            value={plantaSel}
            onChange={(e) => onPlanta(e.target.value)}
          >
            <option value="todas">Todas ({data.por_planta.length})</option>
            {data.por_planta.map((x) => (
              <option key={x.planta_id} value={x.planta_id}>
                {x.nombre}
              </option>
            ))}
          </Select>
        </div>
        <Filtro label="Cliente" valor={data.kpis.cliente_principal.nombre} />
      </div>
      {conAlerta > 0 ? (
        <span className="text-xs font-medium text-warn">
          {conAlerta} planta{conAlerta > 1 ? 's' : ''} con alerta esta semana
        </span>
      ) : (
        <span className="text-xs text-fg-tertiary">Sin alertas esta semana</span>
      )}
    </div>
  );
}

function Filtro({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className="text-xs text-fg">{valor}</span>
    </div>
  );
}

function KpiRow({ data }: { data: DirectivoResumenSemanal }) {
  const k = data.kpis;
  const delta = k.cumplimiento_consolidado.delta_pp;
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border lg:grid-cols-4">
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Cumplimiento consolidado"
          value={
            k.cumplimiento_consolidado.valor_pct !== null
              ? Math.round(k.cumplimiento_consolidado.valor_pct)
              : '—'
          }
          valueSuffix={k.cumplimiento_consolidado.valor_pct !== null ? '%' : undefined}
          trend={
            delta === null
              ? undefined
              : {
                  direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
                  text: `${delta > 0 ? '+' : ''}${Math.round(delta)} pp vs semana anterior`,
                  tone: delta >= 0 ? 'ok' : 'crit',
                }
          }
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Producción total"
          value={miles(k.produccion_total.cantidad)}
          valueSuffix="paq"
          sub={`${k.produccion_total.plantas} plantas · ${k.produccion_total.lineas} líneas`}
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Plantas con alerta"
          value={k.plantas_alerta.count}
          sub={
            k.plantas_alerta.peor_planta_nombre
              ? `${k.plantas_alerta.peor_planta_nombre}${k.plantas_alerta.peor_linea_label ? ` · ${k.plantas_alerta.peor_linea_label}` : ''}`
              : `de ${k.plantas_alerta.total}`
          }
          subTone={k.plantas_alerta.count > 0 ? 'warn' : 'neutral'}
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Cliente principal"
          value={k.cliente_principal.share_pct !== null ? k.cliente_principal.share_pct : '—'}
          valueSuffix={k.cliente_principal.share_pct !== null ? '% vol.' : undefined}
          sub={`${k.cliente_principal.nombre} · ${miles(k.cliente_principal.cantidad)} paq`}
        />
      </div>
    </div>
  );
}

function TendenciaCumplimiento({ data }: { data: DirectivoResumenSemanal }) {
  // null = todas las plantas; un id = aislar esa planta.
  const [plantaId, setPlantaId] = useState<string | null>(null);
  const t = data.tendencia_semanal;

  // Pivot: una fila por semana, una columna por planta. Guard F1: un
  // cumplimiento > 200% viene de un programa ínfimo (residuo de carga) — no es
  // señal y reventaba el eje hasta 9.624%.
  const chartData = t.semanas.map((s) => {
    const row: Record<string, number | string | null> = { semana: s.label };
    for (const p of t.plantas) {
      const punto = t.puntos.find(
        (x) => x.semana_iso === s.semana_iso && x.planta_id === p.planta_id,
      );
      row[p.planta_id] =
        punto && punto.cumplimiento_pct !== null && punto.cumplimiento_pct <= 200
          ? punto.cumplimiento_pct
          : null;
    }
    return row;
  });

  const plantasVisibles = plantaId ? t.plantas.filter((p) => p.planta_id === plantaId) : t.plantas;
  const colorDe = (id: string): string => {
    const idx = t.plantas.findIndex((p) => p.planta_id === id);
    return PLANTA_COLORS[idx % PLANTA_COLORS.length]!;
  };
  const hayDatos = t.puntos.length > 0;

  return (
    <Card className="p-4">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-fg">
          Cumplimiento por semana · últimas {t.semanas.length} semanas
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-fg-tertiary">Planta</span>
          <Select
            className="h-8 w-auto min-w-[160px] py-1 text-xs"
            value={plantaId ?? ''}
            onChange={(e) => setPlantaId(e.target.value || null)}
          >
            <option value="">Todas las plantas</option>
            {t.plantas.map((p) => (
              <option key={p.planta_id} value={p.planta_id}>
                {p.nombre}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <p className="mb-3 text-xs text-fg-tertiary">
        Cumplimiento vs programa Alpina, semana a semana
      </p>
      {hayDatos ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-fg-tertiary">
            {plantasVisibles.map((p) => (
              <span key={p.planta_id} className="flex items-center gap-1.5">
                <span className="size-2 rounded-sm" style={{ background: colorDe(p.planta_id) }} />
                {p.nombre}
              </span>
            ))}
          </div>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="semana"
                  tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                  axisLine={{ stroke: 'var(--color-border)' }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v, _n, item) => [
                    v === null ? 's/d' : `${Math.round(Number(v))}%`,
                    t.plantas.find((p) => p.planta_id === item?.dataKey)?.nombre ?? '',
                  ]}
                />
                {plantasVisibles.map((p) => (
                  <Line
                    key={p.planta_id}
                    type="monotone"
                    dataKey={p.planta_id}
                    name={p.nombre}
                    stroke={colorDe(p.planta_id)}
                    strokeWidth={2}
                    dot={{ r: 2, fill: colorDe(p.planta_id) }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="flex h-[200px] items-center justify-center text-xs text-fg-tertiary">
          Sin datos de cumplimiento en el rango.
        </div>
      )}
    </Card>
  );
}

function GraficoPorPlanta({ data }: { data: DirectivoResumenSemanal }) {
  const chartData = data.por_planta.map((p) => ({
    nombre: p.nombre,
    programa: p.cumpl_programa_pct ?? 0,
    meta: p.cumpl_meta_pct ?? 0,
    estado: p.estado,
  }));

  return (
    <Card className="p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">
          Cumplimiento por planta · semana {data.semana.numero_iso}
        </h2>
        <div className="flex items-center gap-4 text-[11px] text-fg-tertiary">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-sm bg-fg-tertiary" /> Cumpl. Programa
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-sm bg-accent" /> Cumpl. Meta Interna
          </span>
        </div>
      </div>
      <p className="mb-3 text-xs text-fg-tertiary">Programa Alpina vs meta interna</p>
      <div className="h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 16, right: 8, left: -16, bottom: 0 }}
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="nombre"
              tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'var(--color-surface-overlay)' }}
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v, name) => [
                `${Math.round(Number(v))}%`,
                name === 'programa' ? 'Programa' : 'Meta interna',
              ]}
            />
            <Bar
              dataKey="programa"
              fill="var(--color-fg-tertiary)"
              radius={[2, 2, 0, 0]}
              maxBarSize={42}
            />
            <Bar dataKey="meta" radius={[2, 2, 0, 0]} maxBarSize={42}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={ESTADO_META[d.estado].cssVar} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Comparativa({ plantas }: { plantas: ResumenPlantaSemana[] }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-[13px] font-medium text-fg">Comparativa por planta</h2>
      <Table>
        <THead>
          <TR>
            <TH>Planta</TH>
            <TH>Ubicación</TH>
            <TH className="text-right">Producido</TH>
            <TH className="text-right">Cumpl.</TH>
            <TH className="text-right">Paros</TH>
            <TH className="text-right">Estado</TH>
          </TR>
        </THead>
        <TBody>
          {plantas.map((p) => (
            <TR key={p.planta_id}>
              <TD>
                <span className="flex items-center gap-1.5 font-medium text-fg">
                  <span className={cn('size-2 rounded-full', DOT_COLOR[p.estado])} />
                  {p.nombre}
                </span>
              </TD>
              <TD className="text-fg-tertiary">{p.departamento ?? '—'}</TD>
              <TD className="text-right tabular-nums text-fg">{miles(p.producido)} paq</TD>
              <TD className="text-right tabular-nums text-fg">
                {p.cumpl_programa_pct !== null ? `${Math.round(p.cumpl_programa_pct)} %` : 'n/s'}
              </TD>
              <TD className="text-right tabular-nums text-fg-secondary">
                {p.horas_paro > 0
                  ? `${p.horas_paro.toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`
                  : '—'}
              </TD>
              <TD className="text-right">
                <Badge tone={ESTADO_META[p.estado].tone}>{ESTADO_META[p.estado].label}</Badge>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}

function Eventos({ eventos }: { eventos: DirectivoResumenSemanal['eventos'] }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-[13px] font-medium text-fg">Eventos destacados de la semana</h2>
      <div className="flex flex-col divide-y divide-border">
        {eventos.map((e, i) => (
          <div key={i} className="flex items-start gap-3 py-2.5 first:pt-0">
            <Badge tone={ESTADO_META[e.estado].tone} className="mt-0.5 shrink-0">
              {e.planta_nombre}
            </Badge>
            <span className="text-xs leading-relaxed text-fg-secondary">{e.texto}</span>
          </div>
        ))}
        {eventos.length === 0 ? (
          <span className="py-2 text-xs text-fg-tertiary">Sin eventos esta semana.</span>
        ) : null}
      </div>
    </Card>
  );
}
